/**
 * Layout primitives: a full-canvas centered shell for the auth flow, a page
 * heading, and a simple loading state. Token-driven; all copy supplied by callers.
 */

import React from 'react';
import { useTheme } from '@obrava/ui';

/** Full-viewport canvas that vertically + horizontally centers its child. */
export function CenteredCanvas(props: {
  readonly children: React.ReactNode;
}): React.ReactElement {
  const theme = useTheme();
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.color.canvas,
        padding: theme.spacing.space[6],
        boxSizing: 'border-box',
      }}
    >
      <div style={{ width: '100%', maxWidth: theme.spacing.size.authCardMaxWidth }}>
        {props.children}
      </div>
    </div>
  );
}

/** Screen heading: title + optional subtitle, using the display title recipe. */
export function PageHeading(props: {
  readonly title: string;
  readonly subtitle?: string;
  readonly action?: React.ReactNode;
}): React.ReactElement {
  const theme = useTheme();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing.space[5],
        marginBottom: theme.spacing.space[7],
      }}
    >
      <div>
        <h1
          style={{
            margin: 0,
            fontFamily: theme.typography.family.display,
            fontSize: theme.typography.size.title2,
            fontWeight: theme.typography.weight.medium,
            lineHeight: theme.typography.lineHeight.tight,
            letterSpacing: theme.typography.letterSpacing.tight,
            color: theme.color.textPrimary,
          }}
        >
          {props.title}
        </h1>
        {props.subtitle ? (
          <p
            style={{
              margin: `${theme.spacing.space[2]}px 0 0`,
              fontFamily: theme.typography.family.sans,
              fontSize: theme.typography.size.secondary,
              color: theme.color.textSecondary,
            }}
          >
            {props.subtitle}
          </p>
        ) : null}
      </div>
      {props.action ? <div style={{ flexShrink: 0 }}>{props.action}</div> : null}
    </div>
  );
}

/** Section title inside a form/card. */
export function SectionTitle(props: { readonly children: React.ReactNode }): React.ReactElement {
  const theme = useTheme();
  return (
    <h2
      style={{
        margin: `0 0 ${theme.spacing.space[4]}px`,
        fontFamily: theme.typography.family.sans,
        fontSize: theme.typography.size.body,
        fontWeight: theme.typography.weight.medium,
        color: theme.color.textPrimary,
      }}
    >
      {props.children}
    </h2>
  );
}

/** Centered loading message on the canvas. */
export function LoadingView(props: { readonly label: string }): React.ReactElement {
  const theme = useTheme();
  return (
    <CenteredCanvas>
      <p
        style={{
          textAlign: 'center',
          fontFamily: theme.typography.family.sans,
          fontSize: theme.typography.size.body,
          color: theme.color.textSecondary,
        }}
      >
        {props.label}
      </p>
    </CenteredCanvas>
  );
}
